import { useState } from "react";
import type { FormEvent } from "react";

import { useDialogForm } from "@/ds";

import { useFocoPrimeiroInvalido } from "../../hooks/use-foco-primeiro-invalido";
import type {
  ConflitoIgnorarDialogProps,
  ConflitoIgnorarDialogViewModel,
} from "./types-conflito-ignorar-dialog";

/** Liga o botão de confirmação, que vive no rodapé do Dialog, ao formulário do corpo. */
const FORM_ID = "ignorar-conflito-form";
const MAX_LENGTH_PADRAO = 255;

type Estado = { justificativa: string };

/** Modal "Ignorar Conflito de Alocação" (HU 2434, RN06): justificativa obrigatória. */
export function useConflitoIgnorarDialog({
  descricao,
  mensagemObrigatoria,
  onConfirm,
  onCancel,
  cancelLabel = "Cancelar",
  confirmLabel = "Confirmar",
  maxLength = MAX_LENGTH_PADRAO,
  ocultarDicaObrigatorio = false,
}: ConflitoIgnorarDialogProps): ConflitoIgnorarDialogViewModel {
  const [fieldServerError, setFieldServerError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const { corpoRef, focarPrimeiroInvalido } = useFocoPrimeiroInvalido<HTMLFormElement>();

  const form = useDialogForm<Estado>({
    initial: { justificativa: "" },
    // Só espaços conta como vazio: o front não envia justificativa em branco.
    validate: ({ justificativa }) =>
      justificativa.trim() === "" ? { justificativa: mensagemObrigatoria } : {},
    onSubmit: async ({ justificativa }) => {
      const failure = await onConfirm(justificativa.trim());
      if (!failure) return;

      if (failure.kind === "invalid") setFieldServerError(failure.message);
      else setServerError(failure.message);
    },
  });

  return {
    formId: FORM_ID,
    corpoRef,
    descricao,
    cancelLabel,
    confirmLabel,
    maxLength,
    ocultarDicaObrigatorio,
    justificativa: {
      value: form.values.justificativa,
      error: fieldServerError ?? form.errors.justificativa ?? null,
      digitar: (texto) => {
        setFieldServerError(null);
        form.setValue("justificativa", texto);
      },
    },
    serverError,
    isSubmitting: form.isSubmitting,
    confirmar: (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setFieldServerError(null);
      setServerError(null);
      void form.submit().then((enviado) => {
        if (!enviado) focarPrimeiroInvalido();
      });
    },
    cancelar: () => {
      if (!form.isSubmitting) onCancel();
    },
  };
}
